// ═══════════════════════════════════════════════════════════════
// MailingComposerModal.js — Composer hybride IA-assistée
// ═══════════════════════════════════════════════════════════════
// Session J  : modale plein écran + IA + envoi.
// Session J+1: split live preview always-on + ResizeHandle + toolbar
//              Markdown + raccourcis Ctrl+B/I (P0 du backlog).
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { X, Send, Mail, Sparkles, Edit3, Loader2, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from '../ui/Toast';
import MarkdownToolbar from '../ui/MarkdownToolbar';
import * as mailingApi from '../../services/mailingApi';

const MODELS = [
    { id: 'anthropic/claude-haiku-4-5', label: 'Haiku · rapide', tone: 'court' },
    { id: 'anthropic/claude-sonnet-4-6', label: 'Sonnet · équilibré', tone: 'défaut' },
    { id: 'anthropic/claude-opus-4-7', label: 'Opus · profond', tone: 'long' },
];

const SPLIT_STORAGE_KEY = 'itriia_mailing_composer_split_pct';
const DRAFT_STORAGE_KEY = 'itriia_mailing_composer_draft';
const DEFAULT_EDITOR_PCT = 50;

// Charge le brouillon depuis localStorage. Renvoie null si absent ou parsing KO.
function loadDraft() {
    try {
        const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (!raw) return null;
        const d = JSON.parse(raw);
        if (!d || typeof d !== 'object') return null;
        return d;
    } catch (_) { return null; }
}
function clearDraft() {
    try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (_) {}
}

function MailingComposerModal({ initial, onClose, onSent }) {
    const { isDark } = useTheme();

    // ── Brouillon : restauration au mount ─────────────────────
    // Priorité : `initial` (props parent, non vide) > localStorage draft > défauts.
    // Bug fix : si initial est null/undefined, on tombe sur loadDraft() (pas null !).
    const draft = useMemo(() => {
        const hasInitial = initial && typeof initial === 'object' && Object.keys(initial).length > 0;
        if (hasInitial) return null;
        return loadDraft();
    }, [initial]);
    const [draftRestored, setDraftRestored] = useState(!!draft);

    // ── État central ──────────────────────────────────────────
    const [lists, setLists] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [listmonkListId, setListmonkListId] = useState(initial?.listmonkListId || draft?.listmonkListId || null);
    const [subject, setSubject] = useState(initial?.subject || draft?.subject || '');
    const [bodyMd, setBodyMd] = useState(initial?.bodyMd || draft?.bodyMd || '');
    const [templateSlug, setTemplateSlug] = useState(draft?.templateSlug || 'souverain');
    const [senderName, setSenderName] = useState(draft?.senderName || 'ITRIIA');
    const [senderEmail, setSenderEmail] = useState(draft?.senderEmail || 'noreply@itriia.com');
    const [replyTo, setReplyTo] = useState(initial?.replyTo || draft?.replyTo || '');
    // Domaines autorisés (whitelist serveur). Côté UI on alimente
    // simplement un hint pédagogique. Le backend rejette en 400.
    const ALLOWED_DOMAINS_HINT = 'itriia.com';

    // Scheduling
    const [scheduledAt, setScheduledAt] = useState(initial?.scheduledAt || draft?.scheduledAt || '');
    const [sendMode, setSendMode] = useState(
        (initial?.scheduledAt || draft?.scheduledAt) ? 'scheduled' : (draft?.sendMode || 'now')
    );

    // Toggle aperçu : jour / nuit (utile pour le template Souverain qui est sombre)
    const [previewTone, setPreviewTone] = useState(draft?.previewTone || 'default');

    // ── Layout split ──────────────────────────────────────────
    const [editorPct, setEditorPct] = useState(() => {
        try {
            const stored = parseFloat(localStorage.getItem(SPLIT_STORAGE_KEY));
            if (Number.isFinite(stored) && stored >= 25 && stored <= 75) return stored;
        } catch (_) { /* localStorage indisponible */ }
        return DEFAULT_EDITOR_PCT;
    });
    const [previewMd, setPreviewMd] = useState(initial?.bodyMd || draft?.bodyMd || '');
    const [isDragging, setIsDragging] = useState(false);
    const splitContainerRef = useRef(null);
    const dragStartRef = useRef({ x: 0, startWidth: 0 });

    // ── État IA ───────────────────────────────────────────────
    const [aiOpen, setAiOpen] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiSource, setAiSource] = useState('');
    const [aiModel, setAiModel] = useState(MODELS[1].id);
    const [aiBusy, setAiBusy] = useState(false);

    // ── État envoi ────────────────────────────────────────────
    const [campaignId, setCampaignId] = useState(null);
    const [phase, setPhase] = useState('editing'); // editing | testing | sending | sent
    const [testEmail, setTestEmail] = useState(draft?.testEmail || '');

    const previewIframeRef = useRef(null);
    const textareaRef = useRef(null);

    // ── Chargement listes + templates ─────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const [{ lists }, { templates }] = await Promise.all([
                    mailingApi.listLists(),
                    mailingApi.listTemplates(),
                ]);
                setLists(lists || []);
                setTemplates(templates || []);
                if (!listmonkListId && lists?.length > 0) {
                    setListmonkListId(lists[0].listmonk_list_id);
                }
            } catch (err) {
                toast.error("Impossible de charger les listes / modèles.");
            }
        })();
        // eslint-disable-next-line
    }, []);

    // ── Live preview debouncée 200ms ──────────────────────────
    useEffect(() => {
        const t = setTimeout(() => setPreviewMd(bodyMd), 200);
        return () => clearTimeout(t);
    }, [bodyMd]);

    // ── Persistance largeur split ─────────────────────────────
    useEffect(() => {
        try { localStorage.setItem(SPLIT_STORAGE_KEY, String(editorPct)); } catch (_) {}
    }, [editorPct]);

    // ── Persistance brouillon — debounced + sync au unmount ───
    // Bug précédent : le timeout 600ms était cancel à chaque changement
    // de dep (et au unmount). Si l'user fermait dans les 600ms, le
    // draft était perdu. Solution : ref de l'état + flush synchrone à
    // chaque cleanup ET au unmount.
    const stateRef = useRef({});
    stateRef.current = {
        listmonkListId, subject, bodyMd, templateSlug,
        senderName, senderEmail, replyTo,
        scheduledAt, sendMode, previewTone, testEmail, phase,
    };

    const flushDraft = useCallback(() => {
        const s = stateRef.current;
        if (s.phase === 'sent') return; // après clear, on ne ré-écrit pas
        try {
            if ((s.subject || '').trim() || (s.bodyMd || '').trim()) {
                const draftToSave = {
                    listmonkListId: s.listmonkListId,
                    subject: s.subject,
                    bodyMd: s.bodyMd,
                    templateSlug: s.templateSlug,
                    senderName: s.senderName,
                    senderEmail: s.senderEmail,
                    replyTo: s.replyTo,
                    scheduledAt: s.scheduledAt,
                    sendMode: s.sendMode,
                    previewTone: s.previewTone,
                    testEmail: s.testEmail,
                    savedAt: Date.now(),
                };
                localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftToSave));
            }
        } catch (_) { /* quota plein, ignore */ }
    }, []);

    // Debounced save pendant l'édition (UX : pas trop bavard)
    useEffect(() => {
        if (phase === 'sent') return undefined;
        const t = setTimeout(flushDraft, 600);
        return () => clearTimeout(t);
    }, [listmonkListId, subject, bodyMd, templateSlug, senderName, senderEmail, replyTo, scheduledAt, sendMode, previewTone, testEmail, phase, flushDraft]);

    // Flush synchrone au unmount (fermeture modale, refresh page si possible)
    useEffect(() => {
        const onBeforeUnload = () => flushDraft();
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            flushDraft(); // sauve au unmount React (fermeture modale)
        };
    }, [flushDraft]);

    // ── Resize handle (drag) ──────────────────────────────────
    const onResizeStart = useCallback((e) => {
        e.preventDefault();
        if (!splitContainerRef.current) return;
        setIsDragging(true);
        const editorPane = splitContainerRef.current.querySelector('[data-editor-pane]');
        dragStartRef.current = {
            x: e.clientX,
            startWidth: editorPane?.offsetWidth || 0,
        };
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
    }, []);

    useEffect(() => {
        if (!isDragging) return;
        const onMove = (e) => {
            if (!splitContainerRef.current) return;
            const containerW = splitContainerRef.current.offsetWidth;
            const delta = e.clientX - dragStartRef.current.x;
            const newW = dragStartRef.current.startWidth + delta;
            const pct = (newW / containerW) * 100;
            const clamped = Math.min(Math.max(pct, 25), 75);
            requestAnimationFrame(() => setEditorPct(clamped));
        };
        const onUp = () => {
            setIsDragging(false);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [isDragging]);

    // ── Raccourcis clavier (Ctrl+B / Ctrl+I) ──────────────────
    const handleKeyDown = useCallback((e) => {
        if (!(e.ctrlKey || e.metaKey)) return;
        const key = e.key.toLowerCase();
        if (key !== 'b' && key !== 'i') return;
        e.preventDefault();
        const ta = textareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const value = ta.value;
        const selected = value.slice(start, end);
        const marker = key === 'b' ? '**' : '*';
        const placeholder = key === 'b' ? 'gras' : 'italique';
        const inner = selected || placeholder;
        const newValue = value.slice(0, start) + marker + inner + marker + value.slice(end);
        setBodyMd(newValue);
        const selStart = start + marker.length;
        const selEnd = selStart + inner.length;
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(selStart, selEnd);
        });
    }, []);

    // ── Palette ───────────────────────────────────────────────
    const palette = useMemo(() => ({
        bg: isDark ? '#0f1320' : '#fafafa',
        panel: isDark ? '#161a2c' : '#ffffff',
        border: isDark ? '#252944' : '#e6e6ec',
        text: isDark ? '#e8edf5' : '#1a1a2a',
        muted: isDark ? '#8a91a8' : '#6a6a78',
        accent: '#8b5cf6',
        accentSoft: isDark ? '#1f1a3a' : '#f3eeff',
        success: isDark ? '#4ade80' : '#16a34a',
    }), [isDark]);

    // ── Validation ────────────────────────────────────────────
    const canSubmit = subject.trim().length > 0 && bodyMd.trim().length > 0 && listmonkListId;

    // ── Brouillon IA ──────────────────────────────────────────
    const handleAiGenerate = async () => {
        if (!aiPrompt.trim()) return;
        setAiBusy(true);
        try {
            const out = await mailingApi.draftWithAi({
                prompt: aiPrompt.trim(),
                sourceContent: aiSource.trim() || undefined,
                templateSlug,
                model: aiModel,
            });
            if (out.subject) setSubject(out.subject);
            if (out.bodyMd) setBodyMd(out.bodyMd);
            setAiOpen(false);
            toast.success(`Brouillon généré (${aiModel.split('/').pop()}).`);
        } catch (err) {
            toast.error(err.message || 'Génération IA échouée.');
        } finally {
            setAiBusy(false);
        }
    };

    // ── Création/màj brouillon (avant test ou envoi) ──────────
    const ensureCampaign = async () => {
        if (campaignId) return campaignId;
        const scheduledIso = sendMode === 'scheduled' && scheduledAt
            ? new Date(scheduledAt).toISOString()
            : null;
        // Si template souverain ET toggle Jour activé, on envoie le slug
        // 'souverain-clair' au backend (variante claire du même template).
        const effectiveTemplateSlug = (templateSlug === 'souverain' && previewTone === 'light')
            ? 'souverain-clair'
            : templateSlug;
        const { campaign } = await mailingApi.createCampaign({
            listmonkListId,
            subject: subject.trim(),
            bodyMd,
            templateSlug: effectiveTemplateSlug,
            senderName: senderName.trim() || null,
            senderEmail: senderEmail.trim() || null,
            replyTo: replyTo.trim() || null,
            scheduledAt: scheduledIso,
        });
        setCampaignId(campaign.id);
        return campaign.id;
    };

    const handleSendTest = async () => {
        if (!canSubmit) {
            // Diagnostic explicite plutôt que silence
            if (!subject.trim()) toast.warning('Sujet manquant.');
            else if (!bodyMd.trim()) toast.warning('Le corps de la campagne est vide.');
            else if (!listmonkListId) toast.warning('Sélectionnez une liste destinataire.');
            return;
        }
        const dest = (testEmail || '').trim();
        if (!dest) {
            toast.warning('Indiquez un email pour le test.');
            return;
        }
        setPhase('testing');
        try {
            const id = await ensureCampaign();
            await mailingApi.sendTest(id, dest);
            toast.success(`Test envoyé à ${dest}.`);
            setPhase('editing');
        } catch (err) {
            toast.error(err.message || 'Envoi test échoué.');
            setPhase('editing');
            // Si createCampaign a échoué (ex: CHECK constraint), on reset
            // campaignId pour retenter au prochain clic.
            setCampaignId(null);
        }
    };

    const handleSend = async () => {
        if (!canSubmit) return;
        if (sendMode === 'scheduled') {
            if (!scheduledAt) {
                toast.warning('Choisissez une date et une heure pour programmer la campagne.');
                return;
            }
            const ts = new Date(scheduledAt).getTime();
            if (!Number.isFinite(ts) || ts < Date.now() + 60_000) {
                toast.warning('La date programmée doit être au moins 1 minute dans le futur.');
                return;
            }
        }
        setPhase('sending');
        try {
            const id = await ensureCampaign();
            if (sendMode === 'scheduled') {
                await mailingApi.scheduleCampaign(id);
                setPhase('sent');
                toast.success(`Campagne programmée pour le ${new Date(scheduledAt).toLocaleString('fr-FR')}.`);
            } else {
                await mailingApi.sendCampaign(id);
                setPhase('sent');
            }
            clearDraft(); // brouillon résolu, on nettoie
            setTimeout(() => onSent(), 800);
        } catch (err) {
            toast.error(err.message || (sendMode === 'scheduled' ? 'Programmation échouée.' : 'Envoi campagne échoué.'));
            setPhase('editing');
        }
    };

    // ── Render ─────────────────────────────────────────────────
    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'stretch', justifyContent: 'center', zIndex: 9100, padding: 0,
        }}>
            <div style={{
                background: palette.bg, color: palette.text, width: '100%', height: '100%',
                display: 'flex', flexDirection: 'column',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
            }}>
                {/* Header */}
                <header style={{
                    background: palette.panel, borderBottom: `1px solid ${palette.border}`,
                    padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 14,
                }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: `linear-gradient(135deg, ${palette.accent} 0%, #a78bfa 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Mail size={18} color="#fff" />
                    </div>
                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Composer une campagne</h2>
                    {draftRestored && (
                        <span
                            title="Brouillon restauré depuis ta session précédente"
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                fontSize: 11, fontWeight: 600,
                                color: '#a78bfa',
                                background: 'rgba(167,139,250,0.10)',
                                border: '1px solid rgba(167,139,250,0.30)',
                                borderRadius: 999,
                                padding: '3px 10px',
                                letterSpacing: 0.3,
                            }}
                        >
                            ✓ brouillon restauré
                        </span>
                    )}
                    <div style={{ flex: 1 }} />
                    <button onClick={onClose} style={iconBtn(palette)} title="Fermer (le brouillon est sauvegardé)">
                        <X size={18} />
                    </button>
                </header>

                {/* Bandeau réglages */}
                <div style={{
                    background: palette.panel, borderBottom: `1px solid ${palette.border}`,
                    padding: '14px 24px', display: 'grid',
                    gridTemplateColumns: 'minmax(200px, 1.4fr) minmax(180px, 1fr) minmax(180px, 1fr) auto',
                    gap: 12, alignItems: 'end',
                }}>
                    <Field label="Sujet" palette={palette}>
                        <input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="L'objet de votre newsletter"
                            style={inputStyle(palette)}
                            maxLength={500}
                        />
                    </Field>
                    <Field label="Liste destinataire" palette={palette}>
                        <select
                            value={listmonkListId || ''}
                            onChange={(e) => setListmonkListId(parseInt(e.target.value, 10))}
                            style={inputStyle(palette)}
                        >
                            <option value="" disabled>— sélectionner —</option>
                            {lists.map(l => (
                                <option key={l.listmonk_list_id} value={l.listmonk_list_id}>
                                    {l.name} ({l.subscriber_count || 0})
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Modèle" palette={palette}>
                        <select
                            value={templateSlug}
                            onChange={(e) => setTemplateSlug(e.target.value)}
                            style={inputStyle(palette)}
                        >
                            {templates.map(t => (
                                <option key={t.slug} value={t.slug}>{t.name}</option>
                            ))}
                        </select>
                    </Field>
                    <button onClick={() => setAiOpen(true)} style={{ ...primaryBtn(palette), height: 38 }}>
                        <Sparkles size={15} />
                        Générer avec l'IA
                    </button>
                </div>

                {/* Sub-bandeau expéditeur */}
                <div style={{
                    background: palette.panel, borderBottom: `1px solid ${palette.border}`,
                    padding: '12px 24px', display: 'grid',
                    gridTemplateColumns: '1fr 1.2fr 1.2fr', gap: 12, alignItems: 'end',
                }}>
                    <Field label="Nom expéditeur" palette={palette}>
                        <input
                            value={senderName}
                            onChange={(e) => setSenderName(e.target.value)}
                            placeholder="ITRIIA"
                            style={inputStyle(palette)}
                            maxLength={160}
                        />
                    </Field>
                    <Field label="Email expéditeur" palette={palette}>
                        <input
                            type="email"
                            value={senderEmail}
                            onChange={(e) => setSenderEmail(e.target.value)}
                            placeholder="noreply@itriia.com"
                            style={inputStyle(palette)}
                        />
                    </Field>
                    <Field label="Reply-To (optionnel)" palette={palette}>
                        <input
                            type="email"
                            value={replyTo}
                            onChange={(e) => setReplyTo(e.target.value)}
                            placeholder="pierre@itriia.com"
                            style={inputStyle(palette)}
                        />
                    </Field>
                </div>

                {/* Hint domaines autorisés — visible si email modifié vs défaut OU reply-to renseigné */}
                {(senderEmail.trim() !== 'noreply@itriia.com' || replyTo.trim()) && (
                    <div style={{
                        background: 'rgba(167,139,250,0.06)',
                        borderBottom: `1px solid ${palette.border}`,
                        padding: '8px 24px', fontSize: 11.5, color: palette.muted,
                        display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                        <span style={{ color: '#a78bfa', fontWeight: 600 }}>ℹ</span>
                        <span>
                            Les adresses doivent appartenir à un domaine autorisé : <strong style={{ color: palette.text }}>{ALLOWED_DOMAINS_HINT}</strong>.
                            Pour ajouter un nouvel expéditeur (ex. <code>pierre@itriia.com</code>),
                            voir <code>docs/ISPCONFIG-MAILBOX-SMTP-SETUP.md</code>.
                        </span>
                    </div>
                )}

                {/* Zone principale : split éditeur + preview live */}
                <main
                    ref={splitContainerRef}
                    style={{
                        flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden',
                    }}
                >
                    {/* Pane éditeur */}
                    <section
                        data-editor-pane
                        style={{
                            flexBasis: `${editorPct}%`, flexGrow: 0, flexShrink: 0,
                            padding: '16px 20px 16px 24px', display: 'flex', flexDirection: 'column', gap: 10,
                            minHeight: 0, minWidth: 0, overflow: 'hidden',
                        }}
                    >
                        <MarkdownToolbar
                            textareaRef={textareaRef}
                            onChange={setBodyMd}
                            palette={palette}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: palette.muted, fontSize: 11 }}>
                            <Edit3 size={12} />
                            <span>Markdown · Ctrl+B gras, Ctrl+I italique, ou utilisez la barre d'outils</span>
                        </div>
                        <textarea
                            ref={textareaRef}
                            value={bodyMd}
                            onChange={(e) => setBodyMd(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={`# Bonjour…\n\nVotre message en **markdown** s'écrit ici.\n\n- Premier point\n- Deuxième point\n\n[En savoir plus](https://itriia.com)`}
                            style={{
                                flex: 1, ...inputStyle(palette),
                                fontFamily: 'ui-monospace, SFMono-Regular, "Cascadia Code", Menlo, monospace',
                                fontSize: 14, lineHeight: 1.6, padding: 16, resize: 'none',
                                overflowY: 'auto',
                            }}
                            spellCheck={true}
                        />
                    </section>

                    {/* Resize handle */}
                    <div
                        onMouseDown={onResizeStart}
                        title="Redimensionner"
                        style={{
                            width: 6, cursor: 'col-resize', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            position: 'relative', zIndex: 5,
                        }}
                    >
                        <div style={{
                            width: 2, height: 48, borderRadius: 2,
                            background: isDragging ? palette.accent : palette.border,
                            transition: isDragging ? 'none' : 'background 150ms ease',
                            boxShadow: isDragging ? `0 0 8px ${palette.accent}66` : 'none',
                        }} />
                    </div>

                    {/* Pane preview live */}
                    <section style={{
                        flex: 1, minWidth: 0, padding: '16px 24px 16px 16px',
                        overflow: 'hidden', background: palette.bg,
                        display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0,
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            color: palette.muted, fontSize: 11, height: 26,
                        }}>
                            <span style={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                                Aperçu live
                            </span>
                            <span style={{ opacity: 0.6 }}>· rendu approximatif, le serveur applique le modèle final à l'envoi</span>
                            <div style={{ flex: 1 }} />
                            {templateSlug === 'souverain' && (
                                <PreviewToneToggle
                                    palette={palette}
                                    tone={previewTone === 'default' ? 'dark' : previewTone}
                                    onChange={setPreviewTone}
                                />
                            )}
                        </div>
                        <iframe
                            ref={previewIframeRef}
                            title="Aperçu"
                            srcDoc={generatePreviewSrcDoc({ subject, bodyMd: previewMd, templateSlug, senderName, previewTone })}
                            sandbox="allow-same-origin"
                            style={{
                                flex: 1, width: '100%', minHeight: 0,
                                border: `1px solid ${palette.border}`, borderRadius: 8,
                                background: '#fff',
                                pointerEvents: isDragging ? 'none' : 'auto',
                            }}
                        />
                    </section>
                </main>

                {/* Footer envoi */}
                <footer style={{
                    background: palette.panel, borderTop: `1px solid ${palette.border}`,
                    padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                }}>
                    <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="email pour test"
                        style={{ ...inputStyle(palette), maxWidth: 220 }}
                    />
                    <button
                        onClick={handleSendTest}
                        disabled={!canSubmit || phase !== 'editing'}
                        style={{ ...secondaryBtn(palette), opacity: (!canSubmit || phase !== 'editing') ? 0.5 : 1 }}
                    >
                        {phase === 'testing' ? <Loader2 size={15} className="spin" /> : <Send size={15} />}
                        Envoi test
                    </button>

                    <div style={{ flex: 1 }} />

                    {/* Toggle send mode */}
                    <div style={{
                        display: 'flex', gap: 0, padding: 2,
                        background: 'rgba(167,139,250,0.08)',
                        border: `1px solid ${palette.border}`,
                        borderRadius: 8,
                    }}>
                        <button
                            onClick={() => setSendMode('now')}
                            disabled={phase !== 'editing'}
                            style={{
                                background: sendMode === 'now' ? '#a78bfa' : 'transparent',
                                color: sendMode === 'now' ? '#fff' : palette.muted,
                                border: 'none', padding: '6px 12px',
                                borderRadius: 6, cursor: 'pointer',
                                fontSize: 12, fontWeight: 600,
                                transition: 'all 0.15s',
                            }}
                            title="Envoi immédiat dès clic"
                        >
                            Maintenant
                        </button>
                        <button
                            onClick={() => setSendMode('scheduled')}
                            disabled={phase !== 'editing'}
                            style={{
                                background: sendMode === 'scheduled' ? '#a78bfa' : 'transparent',
                                color: sendMode === 'scheduled' ? '#fff' : palette.muted,
                                border: 'none', padding: '6px 12px',
                                borderRadius: 6, cursor: 'pointer',
                                fontSize: 12, fontWeight: 600,
                                transition: 'all 0.15s',
                            }}
                            title="Listmonk déclenchera l'envoi à l'heure prévue"
                        >
                            Programmer
                        </button>
                    </div>

                    {sendMode === 'scheduled' && (
                        <input
                            type="datetime-local"
                            value={scheduledAt}
                            onChange={(e) => setScheduledAt(e.target.value)}
                            min={(() => {
                                const d = new Date(Date.now() + 5 * 60_000);
                                d.setSeconds(0, 0);
                                return d.toISOString().slice(0, 16);
                            })()}
                            disabled={phase !== 'editing'}
                            style={{ ...inputStyle(palette), maxWidth: 220, fontSize: 12.5 }}
                        />
                    )}

                    <button
                        onClick={handleSend}
                        disabled={!canSubmit || phase !== 'editing' || (sendMode === 'scheduled' && !scheduledAt)}
                        style={{
                            ...primaryBtn(palette),
                            opacity: (!canSubmit || phase !== 'editing' || (sendMode === 'scheduled' && !scheduledAt)) ? 0.5 : 1,
                            padding: '10px 20px',
                        }}
                    >
                        {phase === 'sending' ? <Loader2 size={16} className="spin" /> : phase === 'sent' ? <span>✓</span> : <Send size={16} />}
                        {phase === 'sending'
                            ? (sendMode === 'scheduled' ? 'Programmation…' : 'Envoi…')
                            : phase === 'sent'
                                ? (sendMode === 'scheduled' ? 'Programmée' : 'Envoyée')
                                : (sendMode === 'scheduled'
                                    ? (scheduledAt
                                        ? `Programmer pour le ${new Date(scheduledAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                                        : 'Choisir une date')
                                    : 'Envoyer la campagne')}
                    </button>
                </footer>

                {/* Modale IA */}
                {aiOpen && (
                    <AiDraftModal
                        palette={palette}
                        prompt={aiPrompt}
                        setPrompt={setAiPrompt}
                        source={aiSource}
                        setSource={setAiSource}
                        model={aiModel}
                        setModel={setAiModel}
                        busy={aiBusy}
                        onClose={() => setAiOpen(false)}
                        onSubmit={handleAiGenerate}
                    />
                )}

                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 800ms linear infinite; }`}</style>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// Modale IA (sub-modal)
// ═══════════════════════════════════════════════════════════════
function AiDraftModal({ palette, prompt, setPrompt, source, setSource, model, setModel, busy, onClose, onSubmit }) {
    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9200, padding: 20,
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                background: palette.panel, color: palette.text, border: `1px solid ${palette.border}`,
                borderRadius: 14, width: '100%', maxWidth: 560, padding: 24,
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}>
                <header style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                    <Sparkles size={20} color={palette.accent} />
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>Générer un brouillon</h3>
                    <div style={{ flex: 1 }} />
                    <button onClick={onClose} style={iconBtn(palette)} disabled={busy}>
                        <X size={18} />
                    </button>
                </header>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Field label="Que voulez-vous communiquer ?" palette={palette}>
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="Ex : annonce le lancement bêta d'ITRIIA Mailing pour mes contacts Fondateurs, ton sobre"
                            rows={3}
                            style={{ ...inputStyle(palette), resize: 'vertical' }}
                            autoFocus
                            disabled={busy}
                        />
                    </Field>
                    <Field label="Contenu source à exploiter (optionnel)" palette={palette}>
                        <textarea
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            placeholder="Collez ici un texte, un extrait d'article, des notes…"
                            rows={4}
                            style={{ ...inputStyle(palette), resize: 'vertical', maxHeight: 200 }}
                            disabled={busy}
                        />
                    </Field>
                    <Field label="Modèle" palette={palette}>
                        <select value={model} onChange={(e) => setModel(e.target.value)} style={inputStyle(palette)} disabled={busy}>
                            {MODELS.map(m => (
                                <option key={m.id} value={m.id}>{m.label}</option>
                            ))}
                        </select>
                    </Field>
                    <p style={{ margin: 0, fontSize: 12, color: palette.muted, lineHeight: 1.5 }}>
                        L'IA produit un sujet + un corps en markdown. Vous pourrez l'éditer avant envoi.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button onClick={onClose} style={secondaryBtn(palette)} disabled={busy}>Annuler</button>
                        <button onClick={onSubmit} style={primaryBtn(palette)} disabled={busy || !prompt.trim()}>
                            {busy ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />}
                            {busy ? 'Génération…' : 'Générer'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════
function generatePreviewSrcDoc({ subject, bodyMd, templateSlug, senderName, previewTone = 'default' }) {
    // Preview client-side rapide : on fait un mini render en clair.
    // Pour le rendu HTML email-safe complet, on s'appuie sur le backend
    // au moment de la création de campagne. Ici on rend un aperçu approximatif.
    const escape = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Résolution palette selon template + tone
    // Souverain a 2 versions (dark défaut + light premium réversible).
    // Les autres templates restent fixes (academique = light, direct = light).
    let palette;
    if (templateSlug === 'souverain') {
        const useLight = previewTone === 'light';
        palette = useLight
            ? { bg: '#f4f1ea', panel: '#ffffff', text: '#0f1729', accent: '#0080a8', font: 'Georgia, serif', border: '#e8e4d8' }
            : { bg: '#0f1729', panel: '#131c30', text: '#e8edf5', accent: '#00C8FF', font: 'Georgia, serif', border: '#1f2842' };
    } else if (templateSlug === 'academique') {
        palette = { bg: '#f5f4ef', panel: '#ffffff', text: '#1a1a1a', accent: '#2a2a35', font: 'Georgia, serif', border: '#e0ddd5' };
    } else {
        palette = { bg: '#fafafa', panel: '#ffffff', text: '#1a1a1a', accent: '#8b5cf6', font: 'system-ui, sans-serif', border: '#e6e6ec' };
    }

    // Parse tableaux GFM AVANT le reste (sinon les lignes `|...|` sont mangées).
    let tableProcessed = (bodyMd || '_(votre contenu apparaîtra ici)_');
    tableProcessed = tableProcessed.replace(
        /(^|\n)((?:\|.+\|\n)(?:\|[\s:|-]+\|\n)(?:\|.+\|\n?)+)/g,
        (full, lead, block) => {
            const lines = block.trim().split('\n');
            if (lines.length < 2) return full;
            const headerCells = lines[0].slice(1, -1).split('|').map(c => c.trim());
            const sepLine = lines[1].slice(1, -1).split('|').map(c => c.trim());
            const aligns = sepLine.map(s => /^:.*:$/.test(s) ? 'center' : /:$/.test(s) ? 'right' : 'left');
            const rows = lines.slice(2).map(l => l.slice(1, -1).split('|').map(c => c.trim()));
            const headerHtml = headerCells.map((c, j) =>
                `<th style="padding:10px 12px;text-align:${aligns[j] || 'left'};border-bottom:2px solid #d0d0d0;font-weight:600;background:#f5f5f5;">${c}</th>`
            ).join('');
            const bodyHtml = rows.map((r, ri) => {
                const cells = r.map((c, j) =>
                    `<td style="padding:10px 12px;text-align:${aligns[j] || 'left'};border-bottom:1px solid #ececec;">${c}</td>`
                ).join('');
                return `<tr${ri % 2 === 1 ? ' style="background:#fafafa;"' : ''}>${cells}</tr>`;
            }).join('');
            return `${lead}<table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;margin:18px 0;font-size:14px;"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>\n`;
        }
    );

    const renderedBody = tableProcessed
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
        .replace(/^- (.+)$/gm, '<li>$1</li>')
        .replace(/^(\d+)\. (.+)$/gm, '<oli>$2</oli>')
        .replace(/^---$/gm, '<hr/>')
        // Images ![alt](url){width=X align=Y} AVANT liens et bold/italic
        // Auto-upgrade http://→https://. Suffixe optionnel.
        .replace(/!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)(\{[^}]*\})?/g, (_, alt, u, opts) => {
            const safe = u.replace(/^http:\/\//i, 'https://');
            let width = 'auto', align = 'center';
            if (opts) {
                const w = opts.match(/width=(auto|25|50|75|100)/i);
                const a = opts.match(/align=(left|center|right)/i);
                if (w) width = w[1].toLowerCase();
                if (a) align = a[1].toLowerCase();
            }
            const widthStyle = width === 'auto' ? 'max-width:100%;' : `width:${width}%;max-width:100%;`;
            let alignStyle;
            if (align === 'left') alignStyle = 'float:left;margin:4px 16px 12px 0;';
            else if (align === 'right') alignStyle = 'float:right;margin:4px 0 12px 16px;';
            else alignStyle = 'display:block;margin:12px auto;';
            return `<img src="${safe}" alt="${alt}" style="${widthStyle}height:auto;${alignStyle}border-radius:4px;"/>`;
        })
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
        .split(/\n\n+/)
        .map(p => {
            if (p.startsWith('<h') || p.startsWith('<blockquote') || p.startsWith('<hr') || p.startsWith('<img') || p.startsWith('<table')) return p;
            if (p.startsWith('<li>')) return `<ul>${p}</ul>`;
            if (p.startsWith('<oli>')) return `<ol>${p.replace(/<\/?oli>/g, m => m.includes('/') ? '</li>' : '<li>')}</ol>`;
            return `<p>${p.replace(/\n/g, '<br/>')}</p>`;
        })
        .join('');

    // Style premium spécifique au template Souverain (dark + light premium).
    const isSouverain = templateSlug === 'souverain';
    const isLightSouv = isSouverain && previewTone === 'light';
    const isDarkSouv = isSouverain && previewTone !== 'light';

    // Ombrage / accent variables selon contexte
    const cardShadow = isDarkSouv
        ? '0 24px 60px rgba(0,0,0,0.55), 0 2px 0 rgba(0,200,255,0.08) inset'
        : isLightSouv
            ? '0 18px 50px rgba(15,23,41,0.10), 0 1px 0 rgba(0,128,168,0.10) inset'
            : '0 12px 36px rgba(0,0,0,0.06)';
    const cardBorderTop = isSouverain
        ? `linear-gradient(90deg, transparent 0%, ${palette.accent} 50%, transparent 100%)`
        : 'transparent';
    const tagAccent = palette.accent;
    const accentSoft = isDarkSouv ? 'rgba(0,200,255,0.12)' : isLightSouv ? 'rgba(0,128,168,0.10)' : 'rgba(139,92,246,0.10)';
    const dividerColor = isDarkSouv ? 'rgba(0,200,255,0.20)' : isLightSouv ? 'rgba(0,128,168,0.18)' : 'rgba(0,0,0,0.08)';
    const bodyBg = isLightSouv
        ? `linear-gradient(180deg, #f4f1ea 0%, #ece8de 100%)`
        : isDarkSouv
            ? `linear-gradient(180deg, #0f1729 0%, #0a1020 100%)`
            : palette.bg;

    return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><style>
* { box-sizing: border-box; }
body {
    margin: 0;
    padding: 32px 24px;
    background: ${bodyBg};
    font-family: ${palette.font};
    color: ${palette.text};
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
}
.card {
    max-width: 600px;
    margin: 0 auto;
    background: ${palette.panel};
    padding: ${isSouverain ? '40px 36px 32px' : '32px'};
    border-radius: ${isSouverain ? '4px' : '8px'};
    box-shadow: ${cardShadow};
    position: relative;
}
${isSouverain ? `
.card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${cardBorderTop};
    border-radius: 4px 4px 0 0;
}
.card::after {
    content: '';
    position: absolute;
    bottom: 16px; left: 36px; right: 36px;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${dividerColor}, transparent);
}
` : ''}
h1 {
    margin: 0 0 22px 0;
    font-size: ${isSouverain ? '28px' : '24px'};
    line-height: 1.25;
    letter-spacing: ${isSouverain ? '-0.01em' : 'normal'};
    font-weight: ${isSouverain ? '700' : '600'};
}
h2 {
    margin: 28px 0 12px 0;
    font-size: 19px;
    line-height: 1.35;
    font-weight: 600;
    ${isSouverain ? `letter-spacing: -0.005em;` : ''}
}
h3 {
    margin: 22px 0 10px 0;
    font-size: 16px;
    font-weight: 600;
    ${isSouverain ? `color: ${palette.accent}; text-transform: uppercase; letter-spacing: 1.2px; font-size: 12px;` : ''}
}
p {
    margin: 14px 0;
    line-height: ${isSouverain ? '1.75' : '1.65'};
    font-size: ${isSouverain ? '15px' : '14px'};
}
ul, ol { margin: 14px 0; padding-left: 24px; }
li { margin: 7px 0; line-height: 1.65; }
blockquote {
    margin: 22px 0;
    padding: ${isSouverain ? '14px 20px 14px 22px' : '8px 16px'};
    border-left: ${isSouverain ? '2px' : '3px'} solid ${palette.accent};
    background: ${isSouverain ? accentSoft : 'transparent'};
    border-radius: ${isSouverain ? '0 6px 6px 0' : '0'};
    opacity: ${isSouverain ? '1' : '0.85'};
    font-style: italic;
    ${isSouverain ? `position: relative;` : ''}
}
${isSouverain ? `
blockquote::before {
    content: '\\201C';
    position: absolute;
    left: -2px; top: -8px;
    font-size: 32px;
    color: ${palette.accent};
    opacity: 0.5;
    font-family: Georgia, serif;
}
` : ''}
hr {
    border: none;
    border-top: 1px solid ${palette.accent};
    opacity: ${isSouverain ? '0.25' : '0.3'};
    margin: ${isSouverain ? '32px 0' : '20px 0'};
}
a {
    color: ${palette.accent};
    text-decoration: ${isSouverain ? 'none' : 'underline'};
    border-bottom: ${isSouverain ? `1px solid ${palette.accent}` : 'none'};
    padding-bottom: 1px;
}
strong { font-weight: ${isSouverain ? '600' : '700'}; }
em { font-style: italic; ${isSouverain ? `color: ${palette.accent};` : ''} }
img { max-width: 100%; height: auto; display: block; margin: 16px auto; border-radius: 4px; }
.tag {
    font-size: ${isSouverain ? '10px' : '11px'};
    letter-spacing: ${isSouverain ? '3.5px' : '2px'};
    text-transform: uppercase;
    color: ${tagAccent};
    margin-bottom: ${isSouverain ? '14px' : '6px'};
    font-weight: 600;
    ${isSouverain ? `display: inline-flex; align-items: center; gap: 8px;` : ''}
}
${isSouverain ? `
.tag::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${dividerColor};
    margin-left: 4px;
    min-width: 80px;
}
` : ''}
.foot {
    margin-top: ${isSouverain ? '36px' : '24px'};
    padding-top: 16px;
    border-top: 1px solid ${dividerColor};
    font-size: ${isSouverain ? '11px' : '12px'};
    opacity: 0.7;
    ${isSouverain ? `letter-spacing: 0.3px; text-align: center; font-style: italic;` : ''}
}
</style></head><body>
<div class="card">
<div class="tag">${isSouverain ? 'ITRIIA · Newsletter' : 'ITRIIA · Newsletter'}</div>
<h1>${escape(subject) || 'Votre sujet'}</h1>
${renderedBody}
<div class="foot">${escape(senderName || 'ITRIIA')}${isSouverain ? '' : ' — <em>Aperçu approximatif. Le rendu final est généré côté serveur avec le modèle « ' + escape(templateSlug) + ' ».</em>'}</div>
</div>
</body></html>`;
}

// Toggle Jour/Nuit pour la prévisualisation Souverain — design premium
function PreviewToneToggle({ palette, tone, onChange }) {
    const isLight = tone === 'light';
    return (
        <div style={{
            display: 'inline-flex',
            background: 'rgba(167,139,250,0.08)',
            border: `1px solid ${palette.border}`,
            borderRadius: 999,
            padding: 2,
            gap: 0,
        }}>
            <button
                onClick={() => onChange('dark')}
                title="Aperçu nuit"
                style={{
                    background: !isLight ? '#a78bfa' : 'transparent',
                    color: !isLight ? '#fff' : palette.muted,
                    border: 'none',
                    borderRadius: 999,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 10.5, fontWeight: 600,
                    transition: 'all 0.15s',
                }}
            >
                <Moon size={11} />
                Nuit
            </button>
            <button
                onClick={() => onChange('light')}
                title="Aperçu jour"
                style={{
                    background: isLight ? '#a78bfa' : 'transparent',
                    color: isLight ? '#fff' : palette.muted,
                    border: 'none',
                    borderRadius: 999,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 10.5, fontWeight: 600,
                    transition: 'all 0.15s',
                }}
            >
                <Sun size={11} />
                Jour
            </button>
        </div>
    );
}

const primaryBtn = (p) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: p.accent, color: '#fff', border: 'none', borderRadius: 8,
    padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 4px 10px rgba(139,92,246,0.25)', transition: 'all 150ms ease',
});

const secondaryBtn = (p) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: p.panel, color: p.text,
    border: `1px solid ${p.border}`, borderRadius: 8,
    padding: '7px 13px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
});

const iconBtn = (p) => ({
    background: 'transparent', border: `1px solid ${p.border}`,
    borderRadius: 8, padding: 8, cursor: 'pointer',
    color: p.text, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
});

const inputStyle = (p) => ({
    background: p.bg, color: p.text, border: `1px solid ${p.border}`,
    borderRadius: 8, padding: '9px 12px', fontSize: 14,
    fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box',
});

function Field({ label, palette, children }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: palette.muted, letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</span>
            {children}
        </label>
    );
}

export default MailingComposerModal;
